import { Alert, Box, Button, Card, CardContent, FormControl, Grid, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material"
import InputText from "../../components/InputText"
import { ExpandMoreOutlined } from "@mui/icons-material"
import { PlusIcon } from "../../assets/icons/icons"
import { useContext, useEffect, useState } from "react"
import { useGetInfo } from "../dashboard/hooks"
import { useMutateInviteUser } from "./hooks"
import { useNavigate } from "react-router-dom"
import SnackBarContext from "../../context/SnackBarContext"
import { useAuth0 } from "@auth0/auth0-react"

interface FormData {
    invitees: { lineItemValue: string[] }[]
    type: string
    [key: string]: any // Add this line
}

export const InviteUserNew = (props: any) => {
    const theme = useTheme()
    const userRoles = [
        { value: 'superintendent', label: 'Superintendent' },
        { value: 'vtadmin', label: 'VT Admin' },
    ]
    const [emailError, setEmailError] = useState<any>(null)

    const [authUser, setAuthUser] = useState<any | undefined>(null)
    const { data: userData } = useGetInfo('profile', authUser ? authUser.sub : '')
    const { user } = useAuth0()
    const { mutateAsync: inviteUserAsync } = useMutateInviteUser('useraccounts')
    const [submitting, setSubmitting] = useState<boolean>(false)
    const navigate = useNavigate()
    const { toggleSnackbar } = useContext(SnackBarContext);

    const [formData, setFormData] = useState<FormData>({
        invitees: [{ lineItemValue: ['', ''] }],
        type: '',
    })
    const [lineItems, setLineItems] = useState<any[]>([
        { lineItemValue: ['', ''] },
    ])

    const validateEmail = (email: string) => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailPattern.test(email)
    }

    const handleInputChange = (index: number, fieldIndex: number, value: any) => {
        if (index == 1) {
            const isEmailValid = validateEmail(value)
            if (!isEmailValid && value) setEmailError('Enter valid email.')
            else setEmailError(null)
        }
        if (value !== '') {
            const newLineItems = [...lineItems];
            newLineItems[fieldIndex].lineItemValue[index] = value;
            setLineItems(newLineItems);
        }
    };

    const handleRoleChange = (event: any) => {
        setFormData({
            ...formData,
            type: event.target.value
        });
    };

    const handleAdd = () => {
        if (!emailError) {
            setFormData({
                ...formData,
                invitees: lineItems
            });
            setLineItems([...lineItems, { lineItemValue: ['', ''] }]);
        }
    };

    const handleRemove = (row: any, index: number) => {
        setFormData(prevFormData => {
            const updatedInvitees = [...prevFormData.invitees];
            updatedInvitees.splice(index, 1);
            return {
                ...prevFormData,
                invitees: updatedInvitees
            };
        });
    }

    const handleSubmit = async () => {
        if (formData) {
            const invitationData = {
                account: userData?.account._id,
                invitees: formData.invitees,
                invitor: {
                    name: `${authUser.nickname}`,
                },
                organizationId: userData?.account.organizationId,
                type: formData.type,
            }
            setSubmitting(true)
            await inviteUserAsync(invitationData)
            setSubmitting(false)
            navigate('/user-management')
            toggleSnackbar('User invited successfully', 'success');
        }
    };

    useEffect(() => {
        if (user) {
            setAuthUser(user)
        }
    }, [user])

    return (
        <>
            <Box sx={{ mx: 2, mb: 0 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Typography sx={{ fontSize: 36, fontWeight: 500 }}>
                        Invite User
                    </Typography>
                </Box>

                {emailError && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {emailError}
                    </Alert>
                )}
                <Card
                    sx={{
                        padding: 1,
                        py: 4,
                        boxShadow: 'none',
                        border: '1px solid #EEEEEE',
                        borderRadius: '10px',
                        mb: 3
                    }}>
                    <CardContent sx={{ paddingBottom: '15px !important', paddingTop: 0 }}>
                        <Grid container spacing={2} alignItems='end' sx={{ mb: 2 }}>
                            <Grid item xs={3}>
                                <InputText
                                    fieldname={"Full Name"}
                                    value={lineItems[lineItems.length - 1].lineItemValue[0]}
                                    error={false}
                                    label="Full Name"
                                    required
                                    onChange={(e: any) => handleInputChange(0, lineItems.length - 1, e)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputText
                                    fieldname={"Email Address"}
                                    value={lineItems[lineItems.length - 1].lineItemValue[1]}
                                    error={false}
                                    label="Email Address"
                                    required
                                    onChange={(e: any) => handleInputChange(1, lineItems.length - 1, e)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Box sx={{ marginBottom: '15px' }}>
                                    <Typography sx={{
                                        fontSize: "16px",
                                        color: "#5A525B",
                                        paddingBottom: 0.5,
                                        fontWeight: "500",
                                        fontFamily: "Rza",
                                    }}>User Role</Typography>
                                    <FormControl fullWidth required={true} >
                                        <Select
                                            displayEmpty
                                            id='demo-simple-select'
                                            value={formData.type}
                                            onChange={handleRoleChange}
                                            // onChange={(newValue: any) =>
                                            //     onCompositeFieldChange(newValue.target.value, -1)}
                                            IconComponent={ExpandMoreOutlined}
                                            sx={{
                                                width: '100%',
                                                // border: '1px solid #DBDBDB',
                                                boxShadow: 'none',
                                                padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                                                '& .MuiSelect-select': {
                                                    padding: theme.spacing(0.8, 0, 0.8, 0),
                                                },
                                                '& fieldset, & fieldset:hover': {
                                                    border: '1px solid #DBDBDB !important',
                                                    margin: 0,
                                                    boxShadow: 'none',
                                                },
                                            }}>
                                            {userRoles?.map((item: any) => {
                                                return (
                                                    <MenuItem
                                                        value={item.value}
                                                        sx={{ fontFamily: 'Buenos Aires' }}>
                                                        {item.label}
                                                    </MenuItem>
                                                )
                                            })}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box sx={{ marginBottom: '15px' }}>
                                    <Button
                                        variant='contained'
                                        sx={{
                                            padding: theme.spacing(1.6, 2.5, 1.6, 2.5),
                                            borderRadius: '8px',
                                            textTransform: 'capitalize',
                                            width: '100%',
                                            fontSize: '14px',
                                            backgroundColor: "#59A0A0",
                                            "&:hover": {
                                                backgroundColor: "#59A0A0",
                                            },
                                        }}
                                        onClick={() => handleAdd()}
                                    ><PlusIcon /> <Box sx={{ width: 10 }} />Add
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>

                        {lineItems.length - 1 > 0 &&
                            <TableContainer
                                component={Paper}
                                sx={{ my: 2, boxShadow: 'none' }}>
                                <Table
                                    sx={{
                                        minWidth: 650,
                                        boxShadow: 'none',
                                    }}
                                    aria-label='simple table'>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                '& th': {
                                                    backgroundColor: '#DCDAE1',
                                                    padding: '15px',
                                                    color: '#5F5C5F',
                                                    fontWeight: 550,
                                                    whiteSpace: 'nowrap',
                                                    border: 'none'
                                                },
                                                '& th:first-child': {
                                                    borderRadius: '10px 0px 0px 10px',
                                                },
                                                '& th:last-child': {
                                                    borderRadius: '0px 10px 10px 0px',
                                                },
                                            }}>
                                            <TableCell>NAME</TableCell>
                                            <TableCell>EMAIL</TableCell>
                                            <TableCell>USER ROLE</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody
                                        sx={{
                                            '& td': {
                                                backgroundColor: 'white',
                                                color: '#5F5C5F',
                                                fontWeight: 400,
                                                whiteSpace: 'nowrap',
                                                padding: '8px',
                                                fontSize: 14,
                                            },
                                            '& td:first-child': {
                                                borderRadius: '10px 0px 0px 10px',
                                            },
                                            '& td:last-child': {
                                                borderRadius: '0px 10px 10px 0px',
                                            },
                                        }}>
                                        {(formData?.invitees || []).slice()?.map((row: any, index: any) => {
                                            console.log(row, '<=== row')
                                            console.log(formData?.invitees[0]?.lineItemValue, '<=== formData?.invitees[0]?.lineItemValue')
                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={{
                                                        '& td, & th': {
                                                            border: 0,
                                                            background: index % 2 == 0 ? 'white' : '#F1EFF5',
                                                        },
                                                    }}>
                                                    {row.lineItemValue.map((value: any) => (
                                                        <TableCell>{value}</TableCell>
                                                    ))

                                                    }
                                                    <TableCell>
                                                        <Typography>
                                                            <span style={{
                                                                background: formData?.type == 'vtadmin' ? '#808EB4' : formData?.type == 'superintendent' ? '#726988' : '#B17AB2',
                                                                padding: '6px 15px',
                                                                borderRadius: '8px',
                                                                color: '#fff',
                                                                fontWeight: 550,
                                                                textTransform: 'uppercase',
                                                                fontSize: 12,
                                                                letterSpacing: 1.3
                                                            }}>
                                                                {formData?.type}
                                                            </span>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            sx={{
                                                                padding: '1.5px 12px',
                                                                textTransform: 'capitalize',
                                                                borderRadius: 2,
                                                                boxShadow: 'none',
                                                                minWidth: 0,
                                                                backgroundColor: '#8E221880',
                                                                '&:hover': {
                                                                    backgroundColor: '#8E221880',
                                                                    boxShadow: 'none',
                                                                },
                                                                fontSize: 13,
                                                            }}
                                                            onClick={() => handleRemove(row, index)}
                                                            variant='contained'
                                                            color='secondary'>
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        }

                        <Grid container spacing={2} alignItems='end' sx={{ mb: 2 }}>
                            <Grid item xs={2}>
                                <Button
                                    variant='outlined'
                                    sx={{
                                        fontFamily: 'Circular Std',
                                        color: '#F0845D',
                                        borderColor: '#F0845D',
                                        fontSize: '14px',
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        py: 1.5,
                                        width: '100%',
                                        ':hover': {
                                            borderColor: '#F0845D',
                                        },
                                    }}
                                    onClick={() => {
                                        setFormData({
                                            invitees: [{ lineItemValue: ['', ''] }],
                                            type: '',
                                        });
                                        setLineItems([
                                            { lineItemValue: ['', ''] },
                                        ])
                                    }}>
                                    Cancel
                                </Button>
                            </Grid>
                            <Grid item xs={10}>
                                <Button
                                    variant='contained'
                                    sx={{
                                        padding: theme.spacing(1.6, 2.5, 1.6, 2.5),
                                        borderRadius: '8px',
                                        textTransform: 'capitalize',
                                        width: '100%',
                                        fontSize: '14px',
                                    }}
                                    disabled={lineItems.length - 1 == 0 || submitting}
                                    onClick={handleSubmit}
                                > {'Invite User(s)'}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box >
        </>
    )
}